import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconUsers, IconPlus, IconEdit, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const emptyForm = { name: '', department: '', cbo_code: '', base_salary: '', description: '' };

const Roles = () => {
    const [appId, setAppId] = useState(null);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadRoles();
        loadDepartments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadRoles = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/roles/`);
            setRoles(res.data.roles || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar cargos' });
        }
    };

    const loadDepartments = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/departments/`);
            setDepartments(res.data.departments || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenCreate = () => {
        setEditingRole(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const handleOpenEdit = (role) => {
        setEditingRole(role);
        setForm({
            name: role.name,
            department: role.department || '',
            cbo_code: role.cbo_code || '',
            base_salary: role.base_salary || '',
            description: role.description || ''
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name) {
            setStatus({ type: 'error', message: 'Informe o nome do cargo' });
            return;
        }
        try {
            if (editingRole) {
                await api.patch(`v1/apps/${appId}/roles/${editingRole.id}/`, form);
            } else {
                await api.post(`v1/apps/${appId}/roles/`, form);
            }
            setDialogOpen(false);
            loadRoles();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar cargo' });
        }
    };

    const handleDelete = async (role) => {
        if (!window.confirm(`Excluir o cargo "${role.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/roles/${role.id}/`);
            setRoles((prev) => prev.filter((r) => r.id !== role.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir cargo' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconUsers}
                gradient="blue"
                title="Cargos"
                subtitle="Cargos usados nos cadastros de colaboradores"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Novo Cargo
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Departamento</TableCell>
                                <TableCell>CBO</TableCell>
                                <TableCell>Salário base</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {roles.map((role) => (
                                <TableRow key={role.id} hover>
                                    <TableCell>{role.name}</TableCell>
                                    <TableCell>{role.department_name || '—'}</TableCell>
                                    <TableCell>{role.cbo_code || '—'}</TableCell>
                                    <TableCell>{role.base_salary ? `R$ ${role.base_salary}` : '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(role)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(role)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {roles.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhum cargo cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingRole ? 'Editar cargo' : 'Novo cargo'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Nome"
                            fullWidth
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <TextField
                            select
                            label="Departamento (opcional)"
                            fullWidth
                            value={form.department}
                            onChange={(e) => setForm({ ...form, department: e.target.value })}
                        >
                            <MenuItem value="">Nenhum</MenuItem>
                            {departments.map((department) => (
                                <MenuItem key={department.id} value={department.id}>
                                    {department.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Código CBO (opcional)"
                            fullWidth
                            value={form.cbo_code}
                            onChange={(e) => setForm({ ...form, cbo_code: e.target.value })}
                        />
                        <TextField
                            label="Salário base (opcional)"
                            fullWidth
                            type="number"
                            value={form.base_salary}
                            onChange={(e) => setForm({ ...form, base_salary: e.target.value })}
                        />
                        <TextField
                            label="Descrição (opcional)"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Roles;
